import { ReactComponent as ApiSproketIcon } from 'assets/code-sprocket-icon.svg';
import { ReactComponent as DeveloperPortalIcon } from 'assets/developer-portal-icon.svg';
import { sortByDisplayName } from 'helper-utils';
import { APIProductListSummary } from 'proto/web_app_grpc_pb';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useGetCurrentUser, useListAPIProducts } from '../api/hooks';
import { APICatalogSearch } from './APICatalogSearch';
import { APISummaryCard } from './APISummaryCard';
import { Card } from './Card';
import { Loading } from './Loading';

export const secondsToString = (seconds: number | undefined): string => {
  if (!seconds) {
    return '';
  }
  const d = new Date(1970, 0, 1);
  d.setSeconds(seconds);
  return d.toLocaleString();
};

export const ApisCatalogPage = () => {
  const { apiCatalogInfo, apiCatalogInfoError } = useListAPIProducts();
  const { currentUser, currentUserError } = useGetCurrentUser();
  const [searchFilter, setSearchFilter] = React.useState({
    textFilter: '',
    apiSpecFilter: new Set<string>(),
    environmentFilter: new Set<string>(),
    tagFilter: new Set<string>()
  });

  const filterApiProducts = (apiInfo: APIProductListSummary.AsObject) =>
    !searchFilter.textFilter.length ||
    apiInfo.displayName?.toLowerCase().includes(searchFilter.textFilter.toLowerCase()) ||
    apiInfo.description?.toLowerCase().includes(searchFilter.textFilter.toLowerCase());

  if (!apiCatalogInfo && !apiCatalogInfoError) {
    return <Loading message='Loading...' />;
  }

  return (
    <div className='h-full bg-gray-100 apis-catalog-container '>
      <div className='background-image-holder'>
        <img src={`/portal/banner`} alt={''} />
      </div>
      <div className='px-20 py-10 bg-cool-gray-600 apis-catalog-headings '>
        <div className='flex items-center px-4 mx-auto space-x-4 text-white apis-catalog-title max-w-7xl sm:px-6 lg:px-8'>
          <span className='text-4xl'>APIs</span>
          <ApiSproketIcon className='fill-current' />
        </div>
        <div className='px-4 mx-auto mt-2 apis-catalog-description-container max-w-7xl sm:px-6 lg:px-8'>
          <div className='text-white apis-catalog-description'>
            Browse the list of APIs and documentation in this portal. From here you can get the information you need to
            make API calls.
          </div>
        </div>
      </div>
      <div className='grid grid-cols-4 gap-5 mx-auto mt-4 apis-catalog-list-container max-w-7xl sm:px-6 lg:px-8'>
        {!!apiCatalogInfo?.apiProductsList?.length && (
          <div className='col-span-1 '>
            <APICatalogSearch
              currentFilter={searchFilter}
              updateFilter={setSearchFilter}
              apiTypes={apiCatalogInfo?.searchInfo?.apiTypesList ?? []}
              environments={apiCatalogInfo?.searchInfo?.environmentsList ?? []}
              tags={apiCatalogInfo?.searchInfo?.tagsList ?? []}
            />
          </div>
        )}
        <div className='col-span-3 space-y-6 apis-list'>
          {!!apiCatalogInfo?.apiProductsList?.length ? (
            apiCatalogInfo?.apiProductsList
              ?.filter(filterApiProducts)
              .sort(sortByDisplayName)
              .map(apiInfo => (
                <APISummaryCard
                  key={apiInfo?.metadata?.uid}
                  content={{
                    name: apiInfo?.metadata?.name ?? '',
                    namespace: apiInfo?.metadata?.namespace ?? '',
                    modifiedDateTime: !!apiInfo?.modifiedDate
                      ? secondsToString(apiInfo.modifiedDate.seconds)
                      : 'Unknown',
                    displayName: apiInfo?.displayName ?? '',
                    description: apiInfo?.description ?? '',
                    versions: apiInfo?.versionsList ?? [],
                    imageUrl: apiInfo?.imageUrl ?? ''
                  }}
                  searchFilter={searchFilter}
                />
              ))
              .filter(card => card !== null)
          ) : (
            <>
              <Card>
                <div className='empty-card'>
                  <div className='empty-card-image-holder'>
                    <DeveloperPortalIcon />
                  </div>
                  <div className='empty-card-text'>
                    <div className='empty-card-text-heading'>There are no APIs to display.</div>
                    {!!currentUser || !!apiCatalogInfo?.portalAllowAll ? (
                      <div>If you were expecting APIs here check with your admin.</div>
                    ) : (
                      <div>
                        You need to log in to see the APIs here. <NavLink to='/login/'>Log in now.</NavLink>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
              <div className='empty-list-screen' />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
